/**
 * WEB-3447, заход 2 — дверь ленивого куска градиентов бренда и дополнительных
 * переменных стиль-пака (`styleKit.ts`). Решение владельца 17.10.2026,
 * вариант «б»: градиенты и дополнительные переменные пака — в ленивый кусок,
 * только для воронки, которой они нужны; потолок веса первого экрана не
 * поднимать; эталоны вёрстки не править.
 *
 * Приём — общая дверь `player/lazyModuleDoor.ts` (как у вычисляемых значений
 * и рамки Slimkit): первый экран зовёт функции ниже СИНХРОННО; кусок на руках
 * — работает настоящая функция, нет — запасной ответ «как будто поля нет»
 * (тот же, что настоящая даёт воронке без градиентов и паку без
 * дополнительных полей). ЖДУТ страницы квиза и пейволла
 * (`usePlayerKitDoor`, до первого кадра): воронке, которой кусок нужен,
 * сплошной цвет вместо градиента не мелькнёт ни кадром. Кабинет кладёт кусок
 * статически (`main.tsx`), тестовая обвязка — синхронным добытчиком.
 *
 * Равенство «без куска = с куском» у воронок, которым он не нужен, судит
 * `styleKitDoor.test.ts` по всем пакам и темам каталога; по каждому признаку
 * пака — что он включает «нужен».
 *
 * ГРАНИЦА. Признак градиента ищет `linear-gradient(` в теле воронки целиком
 * (палитра, поля блоков) и роли градиента у темы; новое место градиента вне
 * тела воронки и темы признак не увидит — у покупателя там будет сплошной
 * цвет. Новое поле пака, которое читает `stylePackExtraVars`, обязано попасть
 * в `packNeedsStyleKit` (иначе тест равенства по каталогу ловит его, только
 * если такой пак в каталоге есть). Страницы, которые рисуют воронку без этой
 * двери, градиента не ждут (сейчас таких в плеере нет: стиль воронки
 * собирают только `PublicQuiz` и `PublicPaywall`). Кусок отказал (выкат унёс
 * файл после одной перезагрузки) — воронка рисуется без градиентов.
 */
import type React from 'react';
import { playerKitDoor } from '@/player/playerKitDoor';
import type { FunnelGradients } from './gradient';
import type { StylePack } from './stylePack.types';
import type { Theme } from './theme.types';

/**
 * WEB-3447, заход 3: свой файл и своя дверь у набора сняты — он едет в
 * едином куске плеера (`player/playerKit.ts`, дверь `playerKitDoor`) вместе
 * с вычисляемыми значениями и рамкой Slimkit; ждёт страница одной дверью
 * (`player/usePlayerKitDoor.ts`).
 */
const kit = () => playerKitDoor.get()?.style;

/**
 * Имена тем, что едут куском (`LAZY_THEMES`, themeCatalog.ts): первый экран
 * знает их заранее, чтобы ждать кусок до первого кадра. Сверка двух концов —
 * themeCatalog.test.ts.
 */
export const LAZY_THEME_IDS: readonly string[] = ['blue-gradient-light'];

/** Ленивая тема по имени — из куска; без куска её нет. */
export function lazyThemeById(id: string): Theme | undefined {
  return kit()?.lazyThemes.find((t) => t.id === id);
}

/** Годный градиент из поля автора. Без куска — «как будто поля нет». */
export function authorGradient(raw: string | undefined): string | undefined {
  return kit()?.authorGradient(raw);
}

/** Цвет текста поверх градиента автора. Без куска — нет (цвет прежний). */
export function gradientTextColor(gradient: string | undefined): string | null {
  return kit()?.gradientTextColor(gradient) ?? null;
}

/** Роли градиента → переменные корня воронки. Без куска — ни одного ключа. */
export function gradientsToStyle(
  gradients: FunnelGradients | undefined,
): Record<string, string> {
  return kit()?.gradientsToStyle(gradients) ?? {};
}

/**
 * Кегль заголовка пака: потолок из пака, пол 26 px, ужатие по ширине
 * (WEB-1006). Единственная формула — её читают `stylePackToStyle` и
 * `stylePackExtraVars`; живёт в первом экране, потому что двойник
 * `--h1-size-desktop` нужен каждому паку.
 */
export function h1SizeCss(h1Size: number): string {
  return `min(${h1Size}px, max(26px, calc(var(--vp-w, 100vw) * 0.085)))`;
}

/**
 * Дополнительные переменные пака. Без куска — только десктопные двойники:
 * их CSS читает БЕЗ запасного значения (public-paywall.css), и у пака без
 * `type.desktop` настоящая функция отдаёт ровно эти два ключа в этом порядке.
 */
export function stylePackExtraVars(pack: StylePack): Record<string, string> {
  const k = kit();
  if (k) return k.stylePackExtraVars(pack);
  return {
    '--h1-size-desktop': h1SizeCss(pack.type.h1Size),
    '--heading-align-desktop': pack.type.headingAlign,
  };
}

/** Интерлиньяж заголовка из пака. Без куска — прежние 1.15. */
export function titleLineHeight(
  stylePackId: string | undefined,
): number | string {
  return kit()?.titleLineHeight(stylePackId) ?? 1.15;
}

/** Интерлиньяж и трекинг подзаголовка из пака. Без куска — ничего. */
export function subTitleTypeStyle(
  stylePackId: string | undefined,
): Pick<React.CSSProperties, 'lineHeight' | 'letterSpacing'> {
  return kit()?.subTitleTypeStyle(stylePackId) ?? {};
}

/**
 * Пак задаёт поле, которое читают `stylePackExtraVars` или
 * `subTitleTypeStyle`, — без куска его вид был бы иным.
 */
export function packNeedsStyleKit(pack: StylePack | undefined): boolean {
  if (!pack) return false;
  const { density, type, states } = pack;
  return (
    states.screenEnter === 'fade' ||
    typeof density.screenPadXMobile === 'number' ||
    typeof type.h1LineHeight === 'number' ||
    typeof type.subLineHeight === 'number' ||
    typeof type.subTracking === 'number' ||
    type.desktop !== undefined
  );
}

/**
 * Воронке нужен кусок: градиент в теле (палитра, поля блоков), ленивая
 * тема (градиенты бренда есть только у ленивых тем — themeCatalog.test.ts)
 * или пак с дополнительными полями. Не бросает.
 */
export function funnelNeedsStyleKit(
  body: unknown,
  themeId: unknown,
  packs: ReadonlyArray<StylePack | undefined>,
): boolean {
  if (typeof themeId === 'string' && LAZY_THEME_IDS.includes(themeId))
    return true;
  if (packs.some(packNeedsStyleKit)) return true;
  if (body === null || body === undefined) return false;
  try {
    return (JSON.stringify(body) ?? '').includes('linear-gradient(');
  } catch {
    return false;
  }
}
